import { Link } from 'react-router-dom';
import { Bug, RotateCw } from 'lucide-react';

import { describeSourceError } from './describeSourceError';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getSource } from '@/sources/registry';

export interface SourceErrorBannerProps {
  error: unknown;
  /** Source id, used to build a deep-link to the matching Connect Account page. */
  sourceId?: string;
  /** Optional retry handler. If omitted, no retry button is shown. */
  onRetry?: () => void;
  /** Optional diagnostic action for failures outside the reader toolbar. */
  onCopyDebugReport?: () => void;
  className?: string;
}

/**
 * User-facing error banner. Pattern-matches typed source errors via
 * {@link describeSourceError} into a friendly title + body + CTA. Used by
 * the reader, the comic detail page, and the source landing pages.
 */
export function SourceErrorBanner({
  error,
  sourceId,
  onRetry,
  onCopyDebugReport,
  className,
}: SourceErrorBannerProps) {
  const formLogin = Boolean(sourceId && getSource(sourceId)?.loginWithForm);
  const mapped = describeSourceError(error, sourceId, { formLogin });
  const Icon = mapped.icon;
  const palette =
    mapped.variant === 'destructive'
      ? 'border-destructive/40 bg-destructive/10 text-destructive-foreground'
      : 'border-amber-500/40 bg-amber-500/10';
  return (
    <div
      role="alert"
      className={cn('flex flex-col gap-2 rounded-md border p-3 text-sm', palette, className)}
    >
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="flex-1">
          <p className="font-semibold leading-tight">{mapped.title}</p>
          <p className="mt-0.5 text-xs opacity-90">{mapped.body}</p>
        </div>
      </div>
      {(mapped.ctaTo || onRetry || onCopyDebugReport) && (
        <div className="flex flex-wrap items-center gap-2 pl-6">
          {mapped.ctaTo && (
            <Link to={mapped.ctaTo}>
              <Button size="sm" variant="outline">
                {mapped.ctaLabel ?? 'Open'}
              </Button>
            </Link>
          )}
          {onRetry && (
            <Button size="sm" variant="ghost" onClick={onRetry}>
              <RotateCw className="mr-1 h-3.5 w-3.5" />
              Retry
            </Button>
          )}
          {onCopyDebugReport && (
            <Button size="sm" variant="ghost" onClick={onCopyDebugReport}>
              <Bug className="mr-1 h-3.5 w-3.5" />
              Copy debug report
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
